import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { ScrollToTop } from '@/components/ScrollToTop';
import { AnalyticsTracker } from '@/components/AnalyticsTracker';
import { CookieBanner } from '@/components/CookieBanner';
import HomePage from '@/pages/HomePage';
import { WpPostRedirect, WpArchiveRedirect, NotFoundPage } from '@/components/LegacyRoutes';
import '@/App.css';

// Lazy-loaded routes (code split) for faster initial mobile load
const ComparePage = lazy(() => import('@/pages/ComparePage'));
const BrokerDetailPage = lazy(() => import('@/pages/BrokerDetailPage'));
const BrokerDuelPage = lazy(() => import('@/pages/BrokerDuelPage'));
const BlogPage = lazy(() => import('@/pages/BlogPage'));
const BlogPostPage = lazy(() => import('@/pages/BlogPostPage'));
const TradingGuidePage = lazy(() => import('@/pages/TradingGuidePage'));
const TermsPage = lazy(() => import('@/pages/TermsPage'));
const PrivacyPage = lazy(() => import('@/pages/PrivacyPage'));
const ImprintPage = lazy(() => import('@/pages/ImprintPage'));
const AdminPage = lazy(() => import('@/pages/AdminPage'));
const WidgetsPage = lazy(() => import('@/pages/WidgetsPage'));
// Toaster is only needed for transient toasts → lazy load
const Toaster = lazy(() => import('@/components/ui/sonner').then(m => ({ default: m.Toaster })));

const PageFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="w-8 h-8 rounded-full border-4 border-[hsl(210,100%,56%)] border-t-transparent animate-spin" />
  </div>
);

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider>
        <div className="App">
          <BrowserRouter>
            <ScrollToTop />
            <AnalyticsTracker />
            <main id="main-content">
              <Suspense fallback={<PageFallback />}>
                <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/compare" element={<ComparePage />} />
                  <Route path="/broker/:brokerId" element={<BrokerDetailPage />} />
                  <Route path="/duel" element={<BrokerDuelPage />} />
                  <Route path="/blog" element={<BlogPage />} />
                  <Route path="/blog/:slug" element={<BlogPostPage />} />
                  <Route path="/:lang/blog" element={<BlogPage />} />
                  <Route path="/:lang/blog/:slug" element={<BlogPostPage />} />
                  <Route path="/trading-guide" element={<TradingGuidePage />} />
                  <Route path="/widgets" element={<WidgetsPage />} />
                  <Route path="/terms" element={<TermsPage />} />
                  <Route path="/privacy" element={<PrivacyPage />} />
                  <Route path="/imprint" element={<ImprintPage />} />
                  <Route path="/admin" element={<AdminPage />} />
                  <Route path="/:year/:month/:day/:slug" element={<WpPostRedirect />} />
                  <Route path="/tag/*" element={<WpArchiveRedirect />} />
                  <Route path="/category/*" element={<WpArchiveRedirect />} />
                  <Route path="/author/*" element={<WpArchiveRedirect />} />
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </Suspense>
            </main>
          </BrowserRouter>
          <Suspense fallback={null}>
            <Toaster />
          </Suspense>
          <CookieBanner />
        </div>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
